/**
 * SS4InfoModal — explanatory tooltip showing a sample SS4 / CP 575 letter.
 *
 * Inline SVG mockup with obviously-fake data + "FOR ILLUSTRATION ONLY"
 * watermark. Per Prospect-Onboarding-Plan.md Section 3.
 */

import { useEffect } from 'react';

interface Props {
  onClose: () => void;
}

export default function SS4InfoModal({ onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">What does an SS4 look like?</h3>
          <button
            onClick={onClose}
            className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
          Often called an "EIN letter" or "CP&nbsp;575" &mdash; this is what the IRS sends when
          you get an EIN.
        </p>

        <Ss4Mockup />

        <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
          If you can't find your SS4 letter, you can ask the IRS to reissue one at{' '}
          <a
            href="https://www.irs.gov/businesses/small-businesses-self-employed/lost-or-misplaced-your-ein"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            irs.gov
          </a>{' '}
          or call 1-800-829-4933.
        </p>
      </div>
    </div>
  );
}

function Ss4Mockup() {
  return (
    <div className="border border-slate-300 dark:border-slate-600 rounded-md overflow-hidden bg-white">
      <svg
        viewBox="0 0 600 540"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto block"
        aria-label="Sample SS4 / CP 575 letter (for illustration only)"
        role="img"
      >
        {/* Document background */}
        <rect width="600" height="540" fill="#ffffff" />

        {/* Watermark — behind all content */}
        <text
          x="300"
          y="290"
          textAnchor="middle"
          fontSize="42"
          fontWeight="bold"
          fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
          fill="#e5e7eb"
          opacity="0.7"
          transform="rotate(-18 300 290)"
        >
          FOR ILLUSTRATION ONLY
        </text>

        {/* Top-left: IRS letterhead */}
        <g fontFamily="serif" fill="#111827">
          {/* IRS shield placeholder */}
          <rect x="30" y="30" width="32" height="36" fill="#1f2937" rx="2" />
          <text x="46" y="55" textAnchor="middle" fontSize="22" fontWeight="bold" fill="#ffffff" fontFamily="serif">
            IRS
          </text>

          <text x="74" y="42" fontSize="11" fontWeight="600">
            DEPARTMENT OF THE TREASURY
          </text>
          <text x="74" y="56" fontSize="11" fontWeight="600">
            INTERNAL REVENUE SERVICE
          </text>
          <text x="74" y="70" fontSize="11">
            CINCINNATI OH 45999-0023
          </text>
        </g>

        {/* Top-right: notice info */}
        <g fontFamily="serif" fill="#111827" fontSize="11">
          <text x="370" y="42">Date of this notice:</text>
          <text x="495" y="42" fontWeight="600">01-15-2024</text>

          {/* EIN label wraps to 2 lines so the value has room */}
          <text x="370" y="62">Employer Identification</text>
          <text x="370" y="76">Number:</text>
          <text x="495" y="76" fontWeight="600">99-9999999</text>

          <text x="370" y="96">Form: <tspan fontWeight="600">SS-4</tspan></text>

          <text x="370" y="116">Number of this notice: <tspan fontWeight="600">CP 575 G</tspan></text>

          <text x="370" y="142">For assistance you may call us at:</text>
          <text x="370" y="156" fontWeight="600">1-800-829-4933</text>

          <text x="370" y="182">IF YOU WRITE, ATTACH THE</text>
          <text x="370" y="196">STUB AT THE END OF THIS NOTICE.</text>
        </g>

        {/* Centered addressee block */}
        <g fontFamily="serif" fill="#111827" fontSize="12">
          <text x="160" y="180" fontWeight="600">SAMPLE COMPANY LLC</text>
          <text x="160" y="198">JANE SAMPLE OWNER</text>
          <text x="160" y="216">% JANE SAMPLE OWNER</text>
          <text x="160" y="234">1234 EXAMPLE STREET</text>
          <text x="160" y="252">ANYTOWN, ST 12345</text>
        </g>

        {/* Divider */}
        <line x1="30" y1="290" x2="570" y2="290" stroke="#d1d5db" strokeWidth="1" />

        {/* Bold heading */}
        <text
          x="300"
          y="325"
          textAnchor="middle"
          fontFamily="serif"
          fontSize="14"
          fontWeight="bold"
          fill="#111827"
        >
          WE ASSIGNED YOU AN EMPLOYER IDENTIFICATION NUMBER
        </text>

        {/* Body paragraph */}
        <g fontFamily="serif" fill="#374151" fontSize="11">
          <text x="60" y="365">Thank you for applying for an Employer Identification Number</text>
          <text x="60" y="382">(EIN). We assigned you EIN 99-9999999. This EIN will identify</text>
          <text x="60" y="399">you, your business accounts, tax returns, and documents,</text>
          <text x="60" y="416">even if you have no employees. Please keep this notice</text>
          <text x="60" y="433">in your permanent records.</text>

          <text x="60" y="463">When filing tax documents, payments, and related</text>
          <text x="60" y="480">correspondence, it is very important that you use your</text>
          <text x="60" y="497">EIN and complete name and address exactly as shown above.</text>
        </g>
      </svg>
    </div>
  );
}
